import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, Injector, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { ImportChannel } from 'exports/interfaces/imports-runner.interface';
import { catchError, combineLatest, debounceTime, filter, forkJoin, map, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { ChannelListingMapping, ChannelSnapshotEntry } from 'settings/interfaces/channel-listing-mapping.interface';
import {
    ActiveSnapshotPaths,
    ChannelSnapshot,
    ChannelSnapshotChangeTypeEnum,
    ListingSnapshotStatusEnum,
    SnapshotDifferenceLocal,
    SnapshotDifferenceResponse,
    SnapshotGenerationStalenessEnum,
    SnapshotGenerationType,
    SnapshotPathEntries,
} from 'settings/interfaces/listing-channel-shanpshot.interface';
import { Supplier } from 'settings/interfaces/supplier.interface';
import { SuppliersService } from 'settings/services/suppliers.service';
import { ZERO_DATE } from 'shared/constants/general-constants';
import { DateComparisonType } from 'shared/enums/dates.enum';
import { OutletTypesEnum } from 'shared/enums/outlet-types.enum';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { CachingService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { compareDateWithDaysAgo } from 'shared/util/dates.utils';
import { ListingsService } from './listings.service';
import { OutletsService } from './outlets.service';
import { RoomOverviewGeneralService } from './rooms/room-overview-general.service';

const LISTING_SNAPSHOTS_API = environment.BASE_URL + 'listingApi/snapshots/';
const DISTRIBUTION_MANAGEMENT_API = environment.BASE_URL + 'distributionManagementApi';

@Injectable({
    providedIn: 'root',
})
export class ListingChannelComparisonService {
    public readonly EXPEDIA_COMPARISON_WARNING_CONTAINERS: string[] = ['roomTypes', 'guaranteeModes'];

    public listingId = signal<string | null>(null);
    public listingId$ = toObservable(this.listingId);

    public outletId = signal<string | null>(null);
    public outletId$ = toObservable(this.outletId);

    public outletType = signal<OutletTypesEnum>(OutletTypesEnum.HOTEL);
    public outletType$ = toObservable(this.outletType);

    public refreshChannelSnapshot$ = new Subject<string | null>();
    public refreshAllChannelSnapshots$ = new Subject<void>();
    private mappingsLoadedAfterReset = signal(true);

    public activeSnapshotId = signal<string | null>(null);
    public activeSnapshotId$ = toObservable(this.activeSnapshotId);

    public tabFieldName = signal<string | null>(null);
    public tabFieldName$ = toObservable(this.tabFieldName);

    private readonly roomOverviewGeneralService = inject(RoomOverviewGeneralService);

    public tabLoaded = signal<number>(0);
    public tabChanged = signal<number>(0);

    public listingMappings$: Observable<ChannelListingMapping[]> = this.listingId$.pipe(
        switchMap((id) => {
            if (id === null) {
                return of([] as ChannelListingMapping[]);
            }
            return this.listingsService.getChannelListingMappings(id).pipe(
                map((mappings: ChannelListingMapping[]) => mappings.filter((mapping) => !!mapping.channelMappedId)),
                tap(() => this.mappingsLoadedAfterReset.set(true)),
            );
        }),
    );

    public channelListingMappings = toSignal(this.listingMappings$, {
        initialValue: [],
    });

    public allowedChannelsForComparison = toSignal(
        combineLatest([this.listingId$, toObservable(this.suppliersService.currentSupplierByBrand)]).pipe(
            filter((value): value is [string, Supplier] => value[0] !== null && !!value[1]?.id),
            switchMap(([, supplier]) =>
                this.http
                    .get<ImportChannel[]>(DISTRIBUTION_MANAGEMENT_API + '/channels-for-snapshot', {
                        params: { supplierId: supplier.id! },
                    })
                    .pipe(
                        catchError((error: HttpErrorResponse) => {
                            console.error('Failed to fetch channels for snapshot:', error);
                            return of([] as ImportChannel[]);
                        }),
                    ),
            ),
        ),
        { initialValue: [] as ImportChannel[] },
    );

    public filteredAllowedChannelListingMappings = computed(() => {
        const mappings = this.channelListingMappings();
        const allowed = this.allowedChannelsForComparison();

        const filteredMappings = mappings.filter((mapping: any) => allowed.some((entry) => entry.value === mapping.channelId));
        return filteredMappings;
    });
    public filteredMappings$ = toObservable(this.filteredAllowedChannelListingMappings);

    activeSnapshotPaths = computed<ActiveSnapshotPaths | null>(() => {
        const featureGroups = this.comparisonSourceModifiedFeatureGroups();
        const activeSnapshot = this.activeChannelSnapshot();

        if (!activeSnapshot?.channelId) {
            return null;
        }

        const merged: ActiveSnapshotPaths = {
            [ChannelSnapshotChangeTypeEnum.MODIFIED]: [],
            [ChannelSnapshotChangeTypeEnum.ADDED]: [],
            [ChannelSnapshotChangeTypeEnum.DELETED]: [],
        };

        const selectedRoomIndex = this.roomOverviewGeneralService.selectedRoomIndex();

        featureGroups?.forEach((featureGroup) => {
            Object.values(ChannelSnapshotChangeTypeEnum).forEach((changeType) => {
                const entries = featureGroup.differences[changeType];
                if (entries?.length) {
                    merged[changeType]!.push(...entries);
                    if (selectedRoomIndex !== null) {
                        merged[changeType]!.push(...this.toSelectedRoomEntries(entries, selectedRoomIndex));
                    }
                }
            });
        });

        return merged;
    });

    // Copies the selected room's entries without the room index, to match the paths of the rooms UI form
    private toSelectedRoomEntries(entries: SnapshotPathEntries, roomIndex: number): SnapshotPathEntries {
        const roomPrefix = `roomType.roomTypes[${roomIndex}].`;
        const roomsUiPrefix = 'roomType.roomTypes.';

        return entries.flatMap((entry) =>
            Object.entries(entry)
                .map(([key, value]) => [this.normalizePathForComparison(key), value] as const)
                .filter(([normalizedKey]) => normalizedKey.startsWith(roomPrefix))
                .map(([normalizedKey, value]) => ({ [roomsUiPrefix + normalizedKey.slice(roomPrefix.length)]: value })),
        );
    }

    private readonly activeChannelSnapshotStream = computed(() => {
        const activeSnapshotId = this.activeSnapshotId();
        const snapshots = this.channelSnapshots();

        if (!activeSnapshotId || snapshots.length === 0) {
            return null;
        }

        return snapshots.find((snapshot) => snapshot.id === activeSnapshotId) || null;
    });

    public newlyGeneratedChannelSnapshot = signal<
        | (Pick<ChannelSnapshot, 'listingId' | 'id' | 'channelId' | 'status' | 'updatedAt'> & Pick<ChannelListingMapping, 'channelName'>)
        | null
    >(null);

    public activeChannelSnapshot = signal<
        | (Pick<ChannelSnapshot, 'listingId' | 'id' | 'channelId' | 'status' | 'updatedAt'> & Pick<ChannelListingMapping, 'channelName'>)
        | null
    >(null);
    public activeChannelSnapshot$ = toObservable(this.activeChannelSnapshot);

    private readonly syncActiveChannelSnapshot = effect(
        () => {
            this.activeChannelSnapshot.set(this.activeChannelSnapshotStream());
        },
        { injector: inject(Injector) },
    );

    public comparisonSourceFeatureGroupId = signal<string | null>(null);
    public comparisonSourceFeatureGroupId$ = toObservable(this.comparisonSourceFeatureGroupId);

    public comparisonSourceFeatureGroupName = signal<string | null>(null);
    public comparisonSourceFeatureGroupName$ = toObservable(this.comparisonSourceFeatureGroupName);

    currentOutletsData = toSignal(
        combineLatest([this.listingId$, this.activeChannelSnapshot$]).pipe(
            debounceTime(0),
            switchMap(([listingId, activeChannelSnapshot]) => {
                if (
                    !this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON] ||
                    !listingId ||
                    !activeChannelSnapshot?.channelId
                ) {
                    return of(null);
                }

                return this.cachingService.wrapCache(
                    'currentOutletsData',
                    () => this.outletsService.getOutletsOfListing(listingId).pipe(catchError(() => of(null))),
                    [listingId],
                    { ttl: 5000 }, // 5 seconds to allow for quick successive calls to be cached, while ensuring data isn't too stale
                );
            }),
        ),
        { initialValue: null },
    );

    comparisonSourceModifiedFeatureGroups = toSignal(
        combineLatest([this.listingId$, this.outletId$, this.outletType$, this.activeChannelSnapshot$]).pipe(
            debounceTime(0),
            switchMap(([listingId, outletId, outletType, activeChannelSnapshot]) => {
                const targetId = outletId ?? listingId;
                if (
                    !this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON] ||
                    !targetId ||
                    (outletId && !outletType) ||
                    !activeChannelSnapshot?.channelId
                ) {
                    return of([]);
                }

                const targetEndpoint = outletId
                    ? 'comparison-source-outlet-modified-feature-groups'
                    : 'comparison-source-modified-feature-groups';

                const url = new URL(`${LISTING_SNAPSHOTS_API}${targetEndpoint}`);

                if (outletId) {
                    url.searchParams.append('outletId', targetId);
                    url.searchParams.append('outletTypeEnum', outletType.toString());
                } else {
                    url.searchParams.append('listingId', targetId);
                }

                url.searchParams.append('channelId', activeChannelSnapshot.channelId);

                const toPathEntries = (source: Record<string, unknown>): SnapshotPathEntries =>
                    Object.entries(source).map(([key, value]) => ({ [key]: value }));

                return this.cachingService.wrapCache(
                    'comparisonSourceModifiedFeatureGroups',
                    () =>
                        this.http.get<SnapshotDifferenceResponse[]>(url.toString()).pipe(
                            map((responses): SnapshotDifferenceLocal[] =>
                                responses.map((response) => ({
                                    id: response.id,
                                    label: response.label,
                                    differences: {
                                        [ChannelSnapshotChangeTypeEnum.ADDED]: toPathEntries(response.differences.onChNotOnChannel),
                                        [ChannelSnapshotChangeTypeEnum.DELETED]: toPathEntries(response.differences.onChannelNotOnCh),
                                        [ChannelSnapshotChangeTypeEnum.MODIFIED]: toPathEntries(response.differences.modified),
                                    },
                                })),
                            ),
                            catchError(() => of([])),
                        ),
                    [url.toString()],
                    { ttl: 5000 }, // 5 seconds to allow for quick successive calls to be cached, while ensuring data isn't too stale
                );
            }),
        ),
        { initialValue: [] as SnapshotDifferenceLocal[] },
    );

    public channelSnapshots: Signal<ChannelSnapshotEntry[]> = toSignal(
        combineLatest([this.listingId$, this.filteredMappings$, this.refreshAllChannelSnapshots$.pipe(startWith(undefined))]).pipe(
            debounceTime(0),
            switchMap(([listingId, mappings]): Observable<ChannelSnapshotEntry[]> => {
                const currentListingIdFromSignal = this.listingId();
                const mappingsLoaded = this.mappingsLoadedAfterReset();

                if (listingId !== currentListingIdFromSignal) {
                    return of([]);
                }

                if (!mappingsLoaded) {
                    return of([]);
                }

                if (!listingId || !mappings || mappings.length === 0) {
                    return of([]);
                }

                const requests$ = mappings.map((mapping) => this.fetchChannelSnapshot(listingId, mapping));

                const initialSnapshots$ = requests$.length > 0 ? forkJoin(requests$).pipe(map((results) => results.flat())) : of([]);

                return initialSnapshots$.pipe(
                    switchMap((initialSnapshots) => {
                        return this.refreshChannelSnapshot$.pipe(
                            filter((value) => value !== null),
                            switchMap((refreshChannelId) => {
                                if (!refreshChannelId) {
                                    return of(initialSnapshots);
                                }

                                const mappingToRefresh = mappings.find((m) => m.channelId === refreshChannelId);
                                if (!mappingToRefresh) {
                                    return of(initialSnapshots);
                                }

                                return this.fetchChannelSnapshot(listingId, mappingToRefresh).pipe(
                                    map((refreshedSnapshots) => {
                                        const filteredSnapshots = initialSnapshots.filter(
                                            (snapshot) => snapshot.channelId !== refreshChannelId,
                                        );
                                        return [...filteredSnapshots, ...refreshedSnapshots];
                                    }),
                                );
                            }),
                            startWith(initialSnapshots),
                        );
                    }),
                );
            }),
            catchError(() => {
                return of([]);
            }),
        ),
        { initialValue: [] },
    );

    constructor(
        private listingsService: ListingsService,
        private http: HttpClient,
        private featureFlagService: FeatureFlagService,
        private suppliersService: SuppliersService,
        private outletsService: OutletsService,
        private cachingService: CachingService,
    ) {}

    private fetchChannelSnapshot(listingId: string, mapping: ChannelListingMapping): Observable<ChannelSnapshotEntry[]> {
        const url = `${LISTING_SNAPSHOTS_API}listing/${listingId}/channel/${mapping.channelId}/has-active`;

        return this.cachingService.wrapCache(
            'fetchChannelSnapshot',
            () =>
                this.http.get<any>(url).pipe(
                    map((response: any) => {
                        const isRolledBackFailure = response.rolledBack === true;

                        if (isRolledBackFailure) {
                            const failedEntry: ChannelSnapshotEntry = {
                                id: response.id,
                                channelId: response.channelId,
                                channelName: mapping.channelName,
                                listingId: response.listingId,
                                status: ListingSnapshotStatusEnum.FAILED,
                                createdAt: response.updatedAt,
                                dateStale: this.calculateStaleness(response.updatedAt),
                            };

                            const rolledBackEntry: ChannelSnapshotEntry = {
                                id: response.id,
                                channelId: response.channelId,
                                channelName: mapping.channelName,
                                listingId: response.listingId,
                                status: ListingSnapshotStatusEnum.ROLLEDBACK,
                                createdAt: response.createdAt,
                                dateStale: this.calculateStaleness(response.createdAt),
                            };

                            return [failedEntry, rolledBackEntry];
                        }

                        const isFailed = response.status === ListingSnapshotStatusEnum.FAILED;
                        const displayDate = isFailed ? response.updatedAt : response.createdAt;

                        const entry: ChannelSnapshotEntry = {
                            id: response.id,
                            channelId: response.channelId,
                            channelName: mapping.channelName,
                            listingId: response.listingId,
                            status: response.status,
                            createdAt: displayDate,
                            dateStale: this.calculateStaleness(displayDate),
                        };

                        return [entry];
                    }),
                    catchError((error: any) => {
                        if (error.status === 404) {
                            const missingEntry: ChannelSnapshotEntry = {
                                id: '',
                                channelId: mapping.channelId,
                                channelName: mapping.channelName,
                                listingId: listingId,
                                status: ListingSnapshotStatusEnum.MISSING,
                                createdAt: '-',
                            };
                            return of([missingEntry]);
                        }

                        const errorEntry: ChannelSnapshotEntry = {
                            id: '',
                            channelId: mapping.channelId,
                            channelName: mapping.channelName,
                            listingId: listingId,
                            status: undefined as any,
                            createdAt: '-',
                        };
                        return of([errorEntry]);
                    }),
                ),
            [url],
            { ttl: 5000 }, // 5 seconds to allow for quick successive calls to be cached, while ensuring data isn't too stale
        );
    }

    private calculateStaleness(dateString: string | undefined): SnapshotGenerationStalenessEnum {
        if (!dateString || dateString === ZERO_DATE) {
            return SnapshotGenerationStalenessEnum.MISSING;
        }
        return compareDateWithDaysAgo(dateString, 1, DateComparisonType.OLDER)
            ? SnapshotGenerationStalenessEnum.STALE
            : SnapshotGenerationStalenessEnum.FRESH;
    }

    enqueueSnapshotGeneration(
        listingId: string | null,
        channelId: string,
        snapshotGenerationType: SnapshotGenerationType,
    ): Observable<any> {
        if (!this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON] || !listingId) {
            return of(null);
        }

        const url = new URL(`${LISTING_SNAPSHOTS_API}enqueue`);
        url.searchParams.append('listingId', listingId);
        url.searchParams.append('channelId', channelId);
        url.searchParams.append('snapshotGenerationType', snapshotGenerationType.toString());

        return this.http.post(url.toString(), undefined).pipe(
            tap(() => {
                this.refreshChannelSnapshot$.next(channelId);
            }),
        );
    }

    public normalizePathForComparison(path: string): string {
        // Collapses duplicate path segments like "outletProfile.outletProfile" → "outletProfile"
        // because some paths are generated with double pathing of the feature group name, and some with single pathing.
        // Handles three contexts where duplication occurs:
        //   - Followed by "[": taxAndFeeTypes.taxAndFeeTypes[0] → taxAndFeeTypes[0]
        //   - Followed by ".": outletProfile.outletProfile.outletStatus → outletProfile.outletStatus
        //   - At end of string: taxAndFeeTypes.taxAndFeeTypes → taxAndFeeTypes
        let normalized = path.replace(/\[[^\]]*?index=(\d+)[^\]]*\]/g, '[$1]');
        normalized = normalized.replace(/(\w+)\.\1(?=\[|\.|$)/g, '$1');
        return normalized;
    }

    public resetService(): void {
        this.mappingsLoadedAfterReset.set(false);
        this.listingId.set(null);
        this.outletId.set(null);
        this.outletType.set(OutletTypesEnum.HOTEL);
        this.refreshChannelSnapshot$.next(null);
        this.activeSnapshotId.set(null);
        this.activeChannelSnapshot.set(null);
        this.refreshAllChannelSnapshots$.next();
        this.tabLoaded.set(0);
    }
}
